import { ChartOptions, Color, ScaleChartOptions, ScaleOptions, TooltipItem, TooltipLabelStyle } from 'chart.js';
import { countDecimals, currentRound, getPowerUnit, getTorqueUnit, powerRound, round, speedRound, timeRound, torqueRound } from '.';
import { AXIS_IDS, POINT_STYLES, TRANSLATIONS, ACCELERATION_TORQUE_WITH_YD_COLOR, CURVE_COLORS, DIAGRAM_COLORS } from '../constants';
import { CurrentUnit, CurveType, SpecialPointType, SpeedUnit, StartingType, TorqueUnit, UnitSystem } from '../enums';
import { CommonData, Coordinate, Curve, SpecialPoint } from '../models';

export const createCurve = (label: string, curveType: CurveType, xPoints: number[], yPoints: number[], specialPoints: SpecialPoint[], isStartingTypeDOL: boolean) => {
    const color = getCurveColor(curveType, isStartingTypeDOL);

    const curve: Curve = {
        curveType,
        specialPointTypes: specialPoints && specialPoints.map(x => x.type),
        data: xPoints.map((xPoint, index) => ({ x: xPoint, y: yPoints[index] } as Coordinate)),
        type: 'line',
        yAxisID: curveType !== CurveType.VsdPowerLimit ? AXIS_IDS.yAxis0 : AXIS_IDS.yAxis1,
        label,
        fill: false,
        backgroundColor: color ?? '',
        borderColor: color ?? '',
        borderWidth: getBorderWidth(curveType, isStartingTypeDOL),
        borderDash: getBorderDash(curveType, isStartingTypeDOL),
        pointBackgroundColor: [],
        pointBorderColor: [],
        pointBorderWidth: [],
        pointHoverBorderWidth: [],
        pointRadius: Array<number>(xPoints.length).fill(0),
        pointHoverRadius: Array<number>(xPoints.length).fill(4),
        pointHoverBackgroundColor: color ?? '',
        pointHoverBorderColor: color ?? '',
        pointHitRadius: 4,
        pointStyle: [],
        order: 1
    };

    if (specialPoints) {
        addSpecialPoints(curve, specialPoints);
    }

    return curve;
};

export const mergeExpandedPoints = (specialPoints: SpecialPoint[]): SpecialPoint[] => {
    if (specialPoints) {
        const motorOperationPoint = specialPoints.find(x => x.type === SpecialPointType.MotorOperation);
        const loadOperationPoint = specialPoints.find(x => x.type === SpecialPointType.LoadOperation);

        if (motorOperationPoint && loadOperationPoint && motorOperationPoint.index === loadOperationPoint.index) {
            const mergedOperationPoint: SpecialPoint = { index: motorOperationPoint.index, type: SpecialPointType.MotorAndLoadOperation };
            const mergedSpecialPoints = specialPoints.filter(x => x.type !== SpecialPointType.MotorOperation && x.type !== SpecialPointType.LoadOperation);

            mergedSpecialPoints.push(mergedOperationPoint);

            return mergedSpecialPoints;
        }
    } 

    return specialPoints;
};

export const getCurveLabel = (curveType: CurveType, startingVoltage: number | null, startingType: StartingType, translate: (x: string[]) => string) => {
    const ydDependentCurveTypes = [
        CurveType.TorqueOverSpeed, CurveType.TorqueOverSpeedWithSpecificVoltage, CurveType.AccelerationTorque, CurveType.AccelerationTorqueWithSpecificVoltage, CurveType.CurrentOverSpeed, CurveType.CurrentOverSpeedWithSpecificVoltage, CurveType.StartUpTime
    ];
    const suffix = startingType === StartingType.YD && ydDependentCurveTypes.some(x => x === curveType)
        ? 'yd'
        : '';

    const label = translate((TRANSLATIONS.diagram.legend as never)[`${CurveType[curveType].toLowerCase()}${suffix}`]);

    return startingVoltage && label.includes('${x}') ? label.replace('${x}', startingVoltage.toString()) : label; 
};

export const getCurveColor = (curveType: CurveType, isStartingTypeDOL: boolean) => {
    const isAccelerationTorqueWithYDCase = !isStartingTypeDOL && curveType === CurveType.AccelerationTorqueWithSpecificVoltage;

    return isAccelerationTorqueWithYDCase ? ACCELERATION_TORQUE_WITH_YD_COLOR : CURVE_COLORS.get(curveType);
};

export const disassambleSpecialPoints = (curves: Curve[]) => {
    const disassembledCurves: Curve[] = [];
    const disassembledMotorAndLoadOperation: SpecialPointType[] = [SpecialPointType.MotorOperation, SpecialPointType.LoadOperation];

    curves.map(curve => {
        disassembledCurves.push({ ...curve });
    });

    if (disassembledCurves && disassembledCurves.length > 0) {
        disassembledCurves.map((curve, index) => {
            if (curve.specialPointTypes && curve.specialPointTypes.some(x => x === SpecialPointType.MotorAndLoadOperation)) {
                disassembledCurves[index].specialPointTypes = disassembledCurves[index].specialPointTypes.filter(x => x !== SpecialPointType.MotorAndLoadOperation);
                disassembledCurves[index].specialPointTypes.push(...disassembledMotorAndLoadOperation);
            }
        });
    }

    return disassembledCurves;
};

const addSpecialPoints = (curve: Curve, specialPoints: SpecialPoint[]) => {
    specialPoints.forEach(specialPoint => {
        const pointStyle = POINT_STYLES.get(specialPoint.type);

        if (pointStyle) {
            const { index } = specialPoint;

            curve.pointStyle[index] = pointStyle.shape;
            curve.pointRadius[index] = pointStyle.radius;
            curve.pointHoverRadius[index] = pointStyle.hoverRadius;
            curve.pointBackgroundColor[index] = pointStyle.backgroundColor;
            curve.pointBorderColor[index] = pointStyle.borderColor;
            curve.pointBorderWidth[index] = pointStyle.borderWidth;
            curve.pointHoverBorderWidth[index] = pointStyle.hoverBorderWidth;
        }
    });
};

const flat = <T>(twoDimensionalArray: T[][]) => twoDimensionalArray.reduce((flattened, array) => flattened.concat(array), []);

export const calculateXAxisMax = (curves: Curve[]) => curves.length && getAxisMax('x', curves) * 1.05;

export const getAxisMax = (axis: keyof Coordinate, curves: Curve[]) => Math.max(...flat(curves.map(x => x.data.map(y => y[axis]))));

export const getRoundedTooltipTitle = (context: any, currentOverTimeDiagramCurveTypes: CurveType[], isAbsoluteScaling: boolean, unit: string) => {
    const firstCurve = context[0].dataset as any;
    const { x: value } = firstCurve.data[context[0].dataIndex];

    return currentOverTimeDiagramCurveTypes.some(x => x === firstCurve.curveType)
        ? `${timeRound(value)} [s]`
        : `${speedRound(value)} ${isAbsoluteScaling ? unit : ''}`;
};

export const getTooltipCurveColors = (context: TooltipItem<'line'>): TooltipLabelStyle => {
    const { borderColor, backgroundColor, borderWidth } = context.dataset;

    return {
        borderColor: borderColor as Color,
        backgroundColor: backgroundColor as Color,
        borderWidth: Number(borderWidth)
    };
};

export const getRoundedTooltipLabel = (context: TooltipItem<'line'>, isAbsoluteScaling: boolean, unitSystem: UnitSystem) => {
    const { curveType, data, label } = context.dataset as any;
    const { y: value } = data[context.dataIndex];
    let roundedValue: number;
    let unit: string;

    switch (curveType) {
        case CurveType.StartUpTime:
        case CurveType.StartUpTimeWithSpecificVoltage:
        case CurveType.StartUpTimeWithYD:
            roundedValue = timeRound(value);
            unit = '[s]';
            break;
        case CurveType.CurrentOverSpeed:
        case CurveType.CurrentOverSpeedWithSpecificVoltage:
        case CurveType.DolStarDeltaCurrent:
        case CurveType.CurrentOverTimeWithY:
        case CurveType.CurrentOverTimeWithD:
        case CurveType.CurrentOverTimeWithYD:
        case CurveType.CurrentOverTimeWithYWithSpecificVoltage:
        case CurveType.CurrentOverTimeWithDWithSpecificVoltage:
            roundedValue = currentRound(value);
            unit = isAbsoluteScaling ? '[A]' : '';
            break;
        case CurveType.LoadPower:
        case CurveType.MotorShaftPower:
        case CurveType.MotorShaftPowerWithSpecificVoltage:
        case CurveType.VsdPowerLimit:
            roundedValue = powerRound(value);
            unit = getPowerUnit(unitSystem);
            break;
        default:
            roundedValue = torqueRound(value);
            unit = isAbsoluteScaling ? getTorqueUnit(unitSystem) : '';
            break;
    }

    return `${label}: ${roundedValue} ${unit}`;
};

export const isAbsoluteScaling = (commonData: CommonData) =>
    commonData.torqueUnit === TorqueUnit.Nm &&
    commonData.speedUnit === SpeedUnit.Rpm
    && commonData.currentUnit === CurrentUnit.A;

export const getTwoYAxis = (diagramOptions: ChartOptions) => {
    const { white, transparentWhite } = DIAGRAM_COLORS;

    return {
        [AXIS_IDS.yAxis0]: {
            ...diagramOptions && diagramOptions.scales && diagramOptions?.scales[AXIS_IDS.yAxis0] as ScaleOptions<'linear'>
        },
        [AXIS_IDS.yAxis1]: {
            position: 'right',
            display: true,
            title: {
                display: true,
                text: '',
                color: white,
                font: {
                    family: 'SiemensSans',
                    size: 14
                }
            },
            ticks: {
                min: 0,
                padding: 10,
                color: white,
                font: {
                    family: 'SiemensSans',
                    size: 14
                }
            },
            grid: {
                color: (ctx: any) => ctx.index === 0 ? white : transparentWhite,
                lineWidth: (ctx: any) => ctx.index === 0 ? 2 : 1,
                drawTicks: false
            }
        }
    } as ScaleChartOptions;
};

export const getTickValue = (value: number, index: number, values: number[]) =>
    index === 0
        ? round(value, Math.max(...values.slice(1, values.length - 1).map(x => countDecimals(Object.values(x)[0]))))
        : index === values.length - 1
            ? ''
            : value;

const getBorderDash = (curveType: CurveType, isStartingTypeDOL: boolean) => {
    let borderDash: number[];

    switch (curveType) {
        case CurveType.TorqueOverSpeedWithSpecificVoltage:
        case CurveType.CurrentOverSpeedWithSpecificVoltage:
        case CurveType.CurrentOverTimeWithDWithSpecificVoltage:
        case CurveType.CurrentOverTimeWithYWithSpecificVoltage:
        case CurveType.MotorShaftPowerWithSpecificVoltage:
        case CurveType.StartUpTimeWithSpecificVoltage:
        case CurveType.StartUpTimeWithYD:
            borderDash = [10, 10];
            break;
        case CurveType.AccelerationTorqueWithSpecificVoltage:
            borderDash = isStartingTypeDOL ? [10, 10] : [];
            break;
        default:
            borderDash = [];
            break;
    }

    return borderDash;
};

const getBorderWidth = (curveType: CurveType, isStartingTypeDOL: boolean) => {
    let borderWidth: number;

    switch (curveType) {
        case CurveType.DolStarDeltaTorque:
        case CurveType.DolStarDeltaCurrent:
            borderWidth = 3;
            break;
        case CurveType.AccelerationTorqueWithSpecificVoltage:
            borderWidth = isStartingTypeDOL ? 2 : 3;
            break;
        default:
            borderWidth = 2;
            break;
    }

    return borderWidth;
};
